/**
 * Sprint T-17.1 · Notification Center · DeliveryDrawer.
 * Panel lateral · muestra detalle completo del delivery.
 * §4.2 · sections: Timing · Provider · Payload · Error · Template · Actor · Intent
 */
import React from "react";
import { X, Copy } from "lucide-react";
import { toast } from "sonner";
import { RelativeTime, AbsoluteTime } from "../../automationCenter/shared/Formatters";
import JsonPreview from "../../automationCenter/shared/JsonPreview";
import ChannelIcon from "./ChannelIcon";
import StatusPill from "./StatusPill";

function Field({ label, children, testId, mono = false }) {
  return (
    <div className="space-y-0.5" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">{label}</div>
      <div className={`text-[11px] ${mono ? "font-mono" : ""} break-all`}>
        {children ?? <span className="text-[var(--ag-text-dim)] italic">—</span>}
      </div>
    </div>
  );
}

function CopyButton({ value, label = "id" }) {
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="rounded border border-[var(--ag-border)] p-0.5 text-[var(--ag-text-dim)] hover:bg-[var(--ag-surface-2)]"
      aria-label={`Copiar ${label}`}
      title={`Copiar ${label}`}
    >
      <Copy className="h-2.5 w-2.5" />
    </button>
  );
}

export default function DeliveryDrawer({ delivery, onClose }) {
  if (!delivery) return null;
  return (
    <div
      data-testid="notif-delivery-drawer"
      className="fixed inset-0 z-[60]"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden
      />
      {/* Panel */}
      <aside
        className="absolute right-0 top-0 h-full w-full max-w-[480px] overflow-y-auto border-l border-[var(--ag-border)] bg-[var(--ag-surface)] shadow-xl"
        role="dialog"
        aria-label="Detalle de entrega"
      >
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--ag-border)] bg-[var(--ag-surface)] px-4 py-3">
          <div className="flex items-center gap-2">
            <ChannelIcon channel={delivery.channel} size={14} />
            <span className="text-sm font-semibold">Delivery detail</span>
            <StatusPill status={delivery.status} />
          </div>
          <button
            type="button"
            data-testid="notif-delivery-drawer-close"
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] p-1 hover:bg-[var(--ag-surface-2)]"
            aria-label="Cerrar"
          >
            <X className="h-3 w-3" />
          </button>
        </header>

        <div className="space-y-4 px-4 py-4">
          {/* Timing */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ag-gold)]">
              Timing
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <Field label="attempt" testId="drawer-attempt" mono>{delivery.attempt}</Field>
              <Field label="latency_ms" mono>
                {delivery.latency_ms != null ? `${delivery.latency_ms} ms` : "—"}
              </Field>
              <Field label="started_at">
                <AbsoluteTime iso={delivery.started_at} />
              </Field>
              <Field label="finished_at">
                <AbsoluteTime iso={delivery.finished_at} />
                {delivery.finished_at && (
                  <div className="text-[10px] text-[var(--ag-text-dim)]">
                    <RelativeTime iso={delivery.finished_at} />
                  </div>
                )}
              </Field>
              {delivery.will_retry_at && (
                <Field label="will_retry_at">
                  <RelativeTime iso={delivery.will_retry_at} />
                </Field>
              )}
            </div>
          </section>

          {/* Provider */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ag-gold)]">
              Provider
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <Field label="provider_name" mono>{delivery.provider_name}</Field>
              <Field label="dry_run" mono>
                {delivery.dry_run ? (
                  <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1 py-0.5 text-[9px] text-amber-500">
                    dry-run
                  </span>
                ) : (
                  "real"
                )}
              </Field>
              <div className="col-span-2">
                <Field label="provider_message_id" mono>
                  <div className="flex items-center gap-1">
                    <span>{delivery.provider_message_id || "—"}</span>
                    <CopyButton value={delivery.provider_message_id} label="message_id" />
                  </div>
                </Field>
              </div>
            </div>
          </section>

          {/* Payload (provider response snapshot · PII redacted server-side) */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ag-gold)]">
              Provider response
            </h4>
            <JsonPreview
              data={delivery.provider_response_snapshot}
              maxChars={200}
              label="provider_response_snapshot"
              testId="drawer-provider-response"
            />
          </section>

          {/* Error (only if failure path) */}
          {(delivery.error_type || delivery.error_message) && (
            <section className="space-y-2">
              <h4 className="text-[10px] font-semibold uppercase tracking-wide text-red-500">
                Error
              </h4>
              <div className="rounded border border-red-500/30 bg-red-500/5 p-2 space-y-1">
                <Field label="error_type" mono>{delivery.error_type}</Field>
                <Field label="error_message" mono>
                  <span className="whitespace-pre-wrap">{delivery.error_message}</span>
                </Field>
              </div>
            </section>
          )}

          {/* Template */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ag-gold)]">
              Template
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Field label="template_id" mono>{delivery.template_id}</Field>
              </div>
              <Field label="topic" mono>{delivery.topic}</Field>
              <Field label="to" mono>{delivery.to}</Field>
            </div>
          </section>

          {/* Actor */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ag-gold)]">
              Actor
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <Field label="user_id" mono>{delivery.user_id}</Field>
              <Field label="tenant_id" mono>{delivery.tenant_id}</Field>
            </div>
          </section>

          {/* Intent · Correlation · Idempotency */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ag-gold)]">
              Intent trace
            </h4>
            <Field label="intent_id" mono>
              <div className="flex items-center gap-1">
                <span>{delivery.intent_id}</span>
                <CopyButton value={delivery.intent_id} label="intent_id" />
              </div>
            </Field>
            <Field label="correlation_id" mono>
              <div className="flex items-center gap-1">
                <span>{delivery.correlation_id || "—"}</span>
                <CopyButton value={delivery.correlation_id} label="correlation_id" />
              </div>
            </Field>
            <Field label="idempotency_key" mono>{delivery.idempotency_key}</Field>
          </section>
        </div>
      </aside>
    </div>
  );
}
